import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import "./Navbar.css";

/* ---------- Site configuration (edit these) ---------- */
const SCHOOL = {
  name: "Academic Group",
  name2:"of Schools & College",
  tagline: "Excellence in Education",
  phone: "+92 304 7630462",
  email: "academicgroupofschools@gmail.com",
  logo: "/images/logo.webp",
};

const NAV_ITEMS = [
  { label: "Home", to: "/", end: true },
  { label: "About", to: "/about" },
  {
    label: "Academics",
    to: "/academics",
    children: [
      { label: "Primary School", to: "/academics/primary" },
      { label: "Middle School", to: "/academics/middle" },
      { label: "High School", to: "/academics/high" },
    ],
  },
  {
    label: "Campuses",
    to: "/campuses",
    children: [
      { label: "Boys Branch", to: "/campuses/boys" },
      { label: "Girls Branch", to: "/campuses/girls" },
      { label: "Rangilpur Branch", to: "/campuses/rangilpur" },
    ],
  },
  { label: "Gallery", to: "/gallery" },
  { label: "Admissions", to: "/admissions" },
  { label: "Careers", to: "/careers" },  // ← NEW: Careers page link
  { label: "Contact", to: "/contact" },
];

/* Must match the CSS breakpoint (hamburger at 1100px and below) */
const DESKTOP_QUERY = "(min-width: 1101px)";

/* ---------- Small inline icons ---------- */
const Icon = ({ children, size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className="nb-icon"
  >
    {children}
  </svg>
);

const Chevron = () => (
  <svg
    className="nb-chevron"
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const PhoneIcon = () => (
  <Icon>
    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" />
  </Icon>
);
const MailIcon = () => (
  <Icon>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-10 6L2 7" />
  </Icon>
);
const UserIcon = () => (
  <Icon>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </Icon>
);
const ArrowIcon = () => (
  <Icon size={15}>
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </Icon>
);

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isDesktop, setIsDesktop] = useState(
    () => window.matchMedia(DESKTOP_QUERY).matches
  );

  const location = useLocation();
  const barRef = useRef(null);
  const closeTimer = useRef(null);
  const progressRef = useRef(null);
  const headerRef = useRef(null);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setOpenDropdown(null);
  }, []);

  /* Close everything on route change */
  useEffect(() => {
    closeMenu();
  }, [location.pathname, closeMenu]);

  /* Scroll: shrink/shadow + progress bar (no re-render per frame) */
  useEffect(() => {
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      setIsScrolled(y > 10);
      const max =
        document.documentElement.scrollHeight - window.innerHeight || 1;
      if (progressRef.current) {
        progressRef.current.style.transform = `scaleX(${Math.min(
          Math.max(y / max, 0),
          1
        )})`;
      }
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  /* Responsive breakpoint */
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = (e) => {
      setIsDesktop(e.matches);
      if (e.matches) closeMenu();
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [closeMenu]);

  /* Lock body scroll while drawer is open */
  useEffect(() => {
    document.body.style.overflow = isOpen && !isDesktop ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, isDesktop]);

  /* Escape + outside click */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closeMenu();
    };
    const onClickOutside = (e) => {
      if (barRef.current && !barRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, [closeMenu]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  /* Publish the live navbar height as --nb-offset on <html>.
     Pages with their own sticky elements (sidebars, tab bars) use it as
     `top: var(--nb-offset)` so they always sit exactly below the navbar,
     even while the navbar shrinks on scroll. */
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const root = document.documentElement;
    const publish = () => {
      const h = `${Math.round(el.getBoundingClientRect().height)}px`;
      root.style.setProperty("--nb-offset", h);
      /* legacy name used by older page CSS (e.g. Admissions.css) */
      root.style.setProperty("--site-nav-offset", h);
    };
    publish();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(publish) : null;
    if (ro) ro.observe(el);
    window.addEventListener("resize", publish);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", publish);
      root.style.removeProperty("--nb-offset");
      root.style.removeProperty("--site-nav-offset");
    };
  }, []);

  const hoverOpen = (label) => {
    if (!isDesktop) return;
    clearTimeout(closeTimer.current);
    setOpenDropdown(label);
  };
  const hoverClose = () => {
    if (!isDesktop) return;
    closeTimer.current = setTimeout(() => setOpenDropdown(null), 140);
  };
  const toggleDropdown = (label) =>
    setOpenDropdown((prev) => (prev === label ? null : label));

  const isSectionActive = (to) => location.pathname.startsWith(to);
  const tel = `tel:${SCHOOL.phone.replace(/\s/g, "")}`;

  return (
    <header
      ref={headerRef}
      className={`nb ${isScrolled ? "nb--scrolled" : ""} ${
        isOpen ? "nb--open" : ""
      }`}
    >
      <div className="nb-progress" ref={progressRef} aria-hidden="true" />

      {/* ---------- Top info bar ---------- */}
      <div className="nb-topbar">
        <div className="nb-topbar-inner">
          <div className="nb-topbar-contact">
            <a href={tel}>
              <PhoneIcon /> {SCHOOL.phone}
            </a>
            <a href={`mailto:${SCHOOL.email}`}>
              <MailIcon /> {SCHOOL.email}
            </a>
          </div>
          <Link to="/admissions" className="nb-notice">
            <span className="nb-pulse" aria-hidden="true" />
            Admissions Open — Apply Today
          </Link>
        </div>
      </div>

      {/* ---------- Main bar ---------- */}
      <div className="nb-bar" ref={barRef}>
        <Link to="/" className="nb-brand" onClick={closeMenu}>
          <span className="nb-logo">
            <img
              src={SCHOOL.logo}
              alt={`${SCHOOL.name} logo`}
              className="nb-logo-img"
              width="52"
              height="52"
            />
          </span>
          <span className="nb-brand-text">
            <strong className="nb-brand-main">{SCHOOL.name}</strong>
            <strong className="nb-brand-sub">{SCHOOL.name2}</strong>
            <small>{SCHOOL.tagline}</small>
          </span>
        </Link>

        <nav
          id="nb-primary-navigation"
          className={`nb-links ${isOpen ? "is-open" : ""}`}
          aria-label="Primary"
        >
          <ul className="nb-list">
            {NAV_ITEMS.map((item, i) =>
              item.children ? (
                <li
                  key={item.label}
                  style={{ "--i": i }}
                  className={`nb-item ${
                    openDropdown === item.label ? "is-open" : ""
                  }`}
                  onMouseEnter={() => hoverOpen(item.label)}
                  onMouseLeave={hoverClose}
                >
                  <button
                    type="button"
                    className={`nb-link nb-dropbtn ${
                      isSectionActive(item.to) ? "is-active" : ""
                    }`}
                    aria-haspopup="true"
                    aria-expanded={openDropdown === item.label}
                    onClick={() => toggleDropdown(item.label)}
                  >
                    <span>{item.label}</span>
                    <Chevron />
                  </button>
                  <ul className="nb-menu">
                    {item.children.map((child, j) => (
                      <li key={child.to} style={{ "--j": j }}>
                        <NavLink
                          to={child.to}
                          className={({ isActive }) =>
                            isActive ? "is-active" : ""
                          }
                          onClick={closeMenu}
                        >
                          {child.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </li>
              ) : (
                <li key={item.label} className="nb-item" style={{ "--i": i }}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      `nb-link ${isActive ? "is-active" : ""}`
                    }
                    onClick={closeMenu}
                  >
                    <span>{item.label}</span>
                  </NavLink>
                </li>
              )
            )}
          </ul>

          <div className="nb-actions" style={{ "--i": NAV_ITEMS.length }}>
            <Link to="/login" className="nb-btn nb-btn--outline" onClick={closeMenu}>
              <UserIcon /> Login
            </Link>
            <Link to="/admissions" className="nb-btn nb-btn--primary" onClick={closeMenu}>
              Apply Now <ArrowIcon />
            </Link>
          </div>

          {/* Contact card inside the drawer (top bar is hidden on phones) */}
          <div className="nb-drawer-contact" style={{ "--i": NAV_ITEMS.length + 1 }}>
            <a href={tel}>
              <PhoneIcon /> {SCHOOL.phone}
            </a>
            <a href={`mailto:${SCHOOL.email}`}>
              <MailIcon /> {SCHOOL.email}
            </a>
          </div>
        </nav>

        <button
          type="button"
          className={`nb-hamburger ${isOpen ? "is-open" : ""}`}
          onClick={() => setIsOpen((v) => !v)}
          aria-label={isOpen ? "Close menu" : "Open menu"}
          aria-expanded={isOpen}
          aria-controls="nb-primary-navigation"
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      <div
        className={`nb-overlay ${isOpen ? "is-open" : ""}`}
        onClick={closeMenu}
        aria-hidden="true"
      />
    </header>
  );
}

export default Navbar;
